import { fetchConfig } from '@theme/utilities';
import { CartUpdateEvent } from '@theme/events';

class CartSellingPlanSelector extends HTMLElement {
  connectedCallback() {
    this.toggleButton = this.querySelector('[data-toggle]');
    this.dropdown = this.querySelector('[data-dropdown]');
    this.error = this.querySelector('[data-error]');
    this.optionButtons = Array.from(this.querySelectorAll('[data-selling-plan-option]'));
    this.planSelect = this.querySelector('[data-selling-plan-select]');
    this.otpButton = this.querySelector('[data-otp-button]');
    // Ensure dropdown starts closed when controlled by the CTA
    if (this.dropdown && this.toggleButton && !this.dropdown.hasAttribute('hidden')) {
      this.dropdown.setAttribute('hidden', '');
    }

    if (this.dropdown && this.planSelect && !this.toggleButton && this.#hasSubscription()) {
      this.dropdown.removeAttribute('hidden');
    }

    this.boundOutsideClick = this.#handleOutsideClick.bind(this);

    this.toggleButton?.addEventListener('click', this.#handleCtaClick);
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('change', this.#handleChange);
    document.addEventListener('click', this.boundOutsideClick);
  }

  disconnectedCallback() {
    this.toggleButton?.removeEventListener('click', this.#handleCtaClick);
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('change', this.#handleChange);
    document.removeEventListener('click', this.boundOutsideClick);
  }

  #handleCtaClick = (event) => {
    event.preventDefault();
    if (!this.dropdown) return;
    const hasSubscription = this.#hasSubscription();
    const firstPlanId = this.#getDefaultSellingPlanId();

    if (hasSubscription && this.dropdown.hasAttribute('hidden')) {
      this.#openDropdown();
      return;
    }
    // If not yet subscribed, auto-apply the first plan and keep the menu closed
    if (!hasSubscription && firstPlanId) {
      this.#updateSellingPlan(firstPlanId);
      return;
    }

    this.#toggleDropdown();
  };

  #openDropdown() {
    if (!this.dropdown) return;
    this.dropdown.removeAttribute('hidden');
    this.toggleButton?.setAttribute('aria-expanded', 'true');
  }

  #toggleDropdown() {
    if (!this.dropdown) return;
    const isHidden = this.dropdown.hasAttribute('hidden');
    this.dropdown.toggleAttribute('hidden', !isHidden);
    this.toggleButton?.setAttribute('aria-expanded', String(isHidden));
  }

  #handleOutsideClick(event) {
    if (!this.toggleButton) return;
    if (!this.dropdown || this.dropdown.hasAttribute('hidden')) return;
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.#closeDropdown();
  }

  #closeDropdown() {
    if (!this.dropdown || !this.toggleButton) return;
    this.dropdown.setAttribute('hidden', '');
    this.toggleButton?.setAttribute('aria-expanded', 'false');
  }

  #handleOptionSelect = (event) => {
    event.preventDefault();
    const button = /** @type {HTMLElement} */ (event.currentTarget);
    const sellingPlanId = button.dataset.sellingPlanId || null;

    if (this.toggleButton) {
      this.#closeDropdown();
    }
    this.#updateSellingPlan(sellingPlanId);
  };

  #handleSelectChange = (event) => {
    event.preventDefault?.();
    event.stopPropagation?.();
    const select = /** @type {HTMLSelectElement} */ (event.currentTarget);
    const sellingPlanId = select.value || null;

    if (this.toggleButton) {
      this.#closeDropdown();
    }
    this.#updateSellingPlan(sellingPlanId);
  };

  #handleChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLSelectElement)) return;
    if (!target.matches('[data-selling-plan-select]')) return;
    event.preventDefault();
    event.stopPropagation();
    this.planSelect = target;
    this.#handleSelectChange({ currentTarget: target });
  };

  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-otp-button], [data-selling-plan-option]') : null;
    if (!target) return;
    event.preventDefault();
    event.stopPropagation();
    if (target.matches('[data-otp-button]')) {
      this.#handleOtpClick({
        currentTarget: target,
        preventDefault: () => event.preventDefault(),
        stopPropagation: () => event.stopPropagation(),
      });
      return;
    }
    if (target.matches('[data-selling-plan-option]')) {
      this.#handleOptionSelect({
        currentTarget: target,
        preventDefault: () => event.preventDefault(),
        stopPropagation: () => event.stopPropagation(),
      });
    }
  };

  #handleOtpClick = (event) => {
    event.preventDefault();
    event.stopPropagation?.();
    const firstPlanId = this.#getDefaultSellingPlanId();
    if (!firstPlanId) return;
    this.#updateSellingPlan(firstPlanId);
  };

  #updateSellingPlan(sellingPlanId) {
    const line = Number(this.dataset.line || 0);
    const lineItemKey = `${this.dataset.lineItemKey || ''}`.trim();
    const quantity = Number(this.dataset.quantity || 1);
    const sectionId = this.dataset.sectionId;

    if (!line && !lineItemKey) return;

    this.#setLoading(true);
    this.#clearError();

    const sectionsToUpdate = new Set();
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    cartItemsComponents.forEach((component) => {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        sectionsToUpdate.add(component.dataset.sectionId);
      }
    });

    const payload = {
      quantity,
      selling_plan: sellingPlanId || null,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    };

    if (lineItemKey) {
      payload.id = lineItemKey;
    } else {
      payload.line = line;
    }

    const body = JSON.stringify(payload);

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => response.text())
      .then((responseText) => {
        const parsedResponse = JSON.parse(responseText);

        if (parsedResponse.errors) {
          throw new Error(parsedResponse.errors);
        }

        return this.#syncPlanPropertyAfterSellingPlanChange(parsedResponse, {
          line,
          quantity,
          sellingPlanId,
          lineItemKey,
          sectionIds: Array.from(sectionsToUpdate),
        }).then((propertySyncResponse) => propertySyncResponse || parsedResponse);
      })
      .then((parsedResponse) => {
        const syncedLineItem = this.#resolveUpdatedLineItem(parsedResponse, {
          line,
          lineItemKey,
          sellingPlanId,
        });
        if (syncedLineItem?.key) {
          this.dataset.lineItemKey = `${syncedLineItem.key}`;
        }

        let newCartItemCount;
        if (sectionId && parsedResponse.sections?.[sectionId]) {
          const newSectionHTML = new DOMParser().parseFromString(parsedResponse.sections[sectionId], 'text/html');
          const countText = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
          if (countText) {
            newCartItemCount = parseInt(countText, 10);
          }
        }

        document.dispatchEvent(
          new CartUpdateEvent(parsedResponse, sectionId || 'cart-selling-plan-selector', {
            itemCount: newCartItemCount,
            source: 'cart-selling-plan-selector',
            sections: parsedResponse.sections,
          })
        );

        // Update local state so CTA behavior stays consistent before morph
        const shouldHaveSubscription = sellingPlanId !== null && sellingPlanId !== '';
        this.dataset.hasSubscription = shouldHaveSubscription ? 'true' : 'false';
        this.dataset.lineItemProperties = JSON.stringify(this.#getUpdatedLineItemProperties(sellingPlanId));
      })
      .catch((error) => {
        this.#showError(error?.message || 'Unable to update subscription. Please try again.');
      })
      .finally(() => {
        this.#setLoading(false);
        this.#closeDropdown();
      });
  }

  #setLoading(isLoading) {
    this.classList.toggle('is-loading', isLoading);
    if (this.toggleButton) {
      this.toggleButton.disabled = isLoading;
      this.toggleButton.setAttribute('aria-busy', String(isLoading));
    }
    const select = this.querySelector('[data-selling-plan-select]');
    this.planSelect = select;
    if (select) {
      select.disabled = isLoading;
    }
  }

  #clearError() {
    if (!this.error) return;
    this.error.textContent = '';
    this.error.setAttribute('hidden', '');
  }

  #showError(message) {
    if (!this.error) return;
    this.error.textContent = message;
    this.error.removeAttribute('hidden');
  }

  #hasSubscription() {
    return this.dataset.hasSubscription === 'true';
  }

  #getDefaultSellingPlanId() {
    const datasetPlanId = `${this.dataset.firstPlanId || ''}`.trim();
    if (datasetPlanId) return datasetPlanId;

    const select = this.querySelector('[data-selling-plan-select]');
    if (!(select instanceof HTMLSelectElement)) return null;

    const firstPlanOption = Array.from(select.options).find((option) => `${option.value || ''}`.trim());
    return firstPlanOption ? `${firstPlanOption.value || ''}`.trim() : null;
  }

  async #syncPlanPropertyAfterSellingPlanChange(parsedResponse, config) {
    const { line, quantity, sellingPlanId, lineItemKey, sectionIds = [] } = config;
    const updatedProperties = this.#getUpdatedLineItemProperties(sellingPlanId);
    const currentProperties = this.#getCurrentLineItemProperties();

    if (JSON.stringify(updatedProperties) === JSON.stringify(currentProperties)) {
      return parsedResponse;
    }

    const syncedLineItem = this.#resolveUpdatedLineItem(parsedResponse, {
      line,
      lineItemKey,
      sellingPlanId,
    });
    const syncedLineItemKey = `${syncedLineItem?.key || ''}`.trim();
    const syncedLine = syncedLineItem ? this.#getLineNumberForItem(parsedResponse, syncedLineItem) : line;

    if (!syncedLineItemKey && !syncedLine) {
      return parsedResponse;
    }

    const propertyPayload = {
      quantity,
      properties: updatedProperties,
      sections: sectionIds.join(','),
      sections_url: window.location.pathname,
    };

    if (syncedLineItemKey) {
      propertyPayload.id = syncedLineItemKey;
    } else {
      propertyPayload.line = syncedLine;
    }

    const propertyResponse = await fetch(
      `${Theme.routes.cart_change_url}`,
      fetchConfig('json', { body: JSON.stringify(propertyPayload) })
    );
    const propertyResponseText = await propertyResponse.text();
    const parsedPropertyResponse = JSON.parse(propertyResponseText);

    if (!propertyResponse.ok || parsedPropertyResponse?.errors) {
      throw new Error(parsedPropertyResponse?.errors || 'Unable to update subscription.');
    }

    if (parsedResponse.sections && !parsedPropertyResponse.sections) {
      parsedPropertyResponse.sections = parsedResponse.sections;
    }

    return parsedPropertyResponse;
  }

  #getUpdatedLineItem(parsedResponse, line) {
    const items = Array.isArray(parsedResponse?.items) ? parsedResponse.items : [];
    if (!items.length || !line) return null;
    return items[line - 1] || null;
  }

  #resolveUpdatedLineItem(parsedResponse, config) {
    const { line, lineItemKey, sellingPlanId } = config;
    const items = Array.isArray(parsedResponse?.items) ? parsedResponse.items : [];
    if (!items.length) return null;

    const expectedVariantId = `${this.dataset.variantId || ''}`.trim();
    const expectedPlanId = `${sellingPlanId || ''}`.trim();
    const keyedItem = items.find((item) => `${item?.key || ''}`.trim() === `${lineItemKey || ''}`.trim());
    if (this.#itemMatchesTargetState(keyedItem, expectedVariantId, expectedPlanId)) {
      return keyedItem;
    }

    const lineItem = this.#getUpdatedLineItem(parsedResponse, line);
    if (this.#itemMatchesTargetState(lineItem, expectedVariantId, expectedPlanId)) {
      return lineItem;
    }

    return (
      items.find((item) => this.#itemMatchesTargetState(item, expectedVariantId, expectedPlanId)) ||
      lineItem ||
      keyedItem ||
      null
    );
  }

  #itemMatchesTargetState(item, expectedVariantId, expectedPlanId) {
    if (!item || typeof item !== 'object') return false;

    if (expectedVariantId && `${item?.variant_id || ''}`.trim() !== expectedVariantId) {
      return false;
    }

    const itemSellingPlanId = `${item?.selling_plan_allocation?.selling_plan?.id || ''}`.trim();
    return itemSellingPlanId === expectedPlanId;
  }

  #getLineNumberForItem(parsedResponse, targetItem) {
    const items = Array.isArray(parsedResponse?.items) ? parsedResponse.items : [];
    const targetKey = `${targetItem?.key || ''}`.trim();
    if (!targetKey) return 0;

    const matchedIndex = items.findIndex((item) => `${item?.key || ''}`.trim() === targetKey);
    return matchedIndex >= 0 ? matchedIndex + 1 : 0;
  }

  #getUpdatedLineItemProperties(sellingPlanId) {
    const properties = this.#getCurrentLineItemProperties();
    const planPropertyValue = this.#getPlanPropertyValue(sellingPlanId);

    if (planPropertyValue) {
      properties._plan = planPropertyValue;
    } else {
      properties._plan = '';
    }

    return properties;
  }

  #getCurrentLineItemProperties() {
    const rawProperties = `${this.dataset.lineItemProperties || ''}`.trim();
    if (!rawProperties) return {};

    try {
      const parsed = JSON.parse(rawProperties);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch (error) {
      console.error('Unable to parse cart line item properties:', error);
      return {};
    }
  }

  #getPlanPropertyValue(sellingPlanId) {
    if (!sellingPlanId) return '';

    const select = this.querySelector('[data-selling-plan-select]');
    if (!(select instanceof HTMLSelectElement)) return '';

    const matchedOption = Array.from(select.options).find((option) => `${option.value || ''}`.trim() === `${sellingPlanId}`.trim());
    const planName = `${matchedOption?.dataset.planName || matchedOption?.textContent || ''}`.trim();
    const matchedDays = planName.match(/(\d+)/);

    if (matchedDays?.[1] === '30') return '30 days';
    if (matchedDays?.[1] === '90') return '90 days';

    return '';
  }
}

if (!customElements.get('cart-selling-plan-selector')) {
  customElements.define('cart-selling-plan-selector', CartSellingPlanSelector);
}
